import { useCallback, useEffect, useState } from 'react';
import { api, PaymentRecord, AdminMember } from './adminApi';
import { inputCls, btnGhost, cardCls } from './AdminPanel';

const STATUSES = ['paid', 'unpaid', 'waived'];

export default function AdminPayments({ onToast }: { onToast: (msg: string) => void }) {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [matches, setMatches] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [pRes, mRes, mmRes] = await Promise.all([
      api<{ payments: PaymentRecord[] }>('/api/admin/payments'),
      api<{ members: AdminMember[] }>('/api/admin/members'),
      api<{ matches: Array<{ id: string; title: string }> }>('/api/admin/matches'),
    ]);
    if (pRes.ok && pRes.data) setPayments(pRes.data.payments);
    if (mRes.ok && mRes.data) {
      const map: Record<string, string> = {};
      mRes.data.members.forEach((m) => { map[m.id] = m.displayName; });
      setMembers(mRes.data.members);
      setMemberNames(map);
    }
    if (mmRes.ok && mmRes.data) {
      const map: Record<string, string> = {};
      mmRes.data.matches.forEach((m) => { map[m.id] = m.title; });
      setMatches(map);
    }
    setLoading(false);
  }, []);

  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  useEffect(() => { load(); }, [load]);

  const setStatus = async (p: PaymentRecord, status: string) => {
    setBusyId(p.id);
    const res = await api(`/api/admin/payments/${p.id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    setBusyId(null);
    if (res.ok) { onToast('Payment updated'); await load(); } else onToast(res.error || 'Update failed');
  };

  if (loading) return <p className="text-sm text-gray-500">Loading payments…</p>;

  const totalUnpaid = payments.filter((p) => p.status === 'unpaid').reduce((s, p) => s + (p.amount || 0), 0);
  const totalPaid = payments.filter((p) => p.status === 'paid').reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className={cardCls}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold">Payments ({payments.length})</h2>
        <div className="text-xs text-gray-500">Paid ৳{totalPaid.toLocaleString('en-BD')} · Unpaid ৳{totalUnpaid.toLocaleString('en-BD')}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Player</th><th className="py-2 pr-3">Match</th><th className="py-2 pr-3">Amount</th><th className="py-2 pr-3">Method</th><th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 pr-3">{memberNames[p.memberProfileId] || p.memberProfileId}</td>
                <td className="py-2 pr-3">{matches[p.matchId] || p.matchId}</td>
                <td className="py-2 pr-3 font-bold">৳{p.amount ?? 0}</td>
                <td className="py-2 pr-3">{p.paymentMethod}</td>
                <td className="py-2">
                  <select className={inputCls + ' !w-auto'} value={p.status} disabled={busyId === p.id} onChange={(e) => setStatus(p, e.target.value)}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {!payments.length && <tr><td colSpan={5} className="py-4 text-center text-xs text-gray-500">No payments recorded yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}